import { createContext, FC, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { useApi } from 'shared/hooks/useApi';
import { DashboardHomeDetailsType } from 'types/DashboardHomeDetails/DashboardHomeDetailsType';
import { mapDtoToDashboardHomeDetailsType } from 'types/DashboardHomeDetails/mapper';
import { JobOrdersHistoryType } from 'types/JobOrdersHistory/JobOrdersHistoryType';
import { mapDtoToJobOrdersHistoryType } from 'types/JobOrdersHistory/mapper';

interface DashboardData {
  home: DashboardHomeDetailsType | null;
  history: JobOrdersHistoryType | null;
}

const DashboardDataContext = createContext<DashboardData>({ home: null, history: null });

export const DashboardDataProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const isHistory = !!useMatch('/:dashboardId/job-orders-history');
  const { data: home, refetch: refetchHome } = useApi<DashboardHomeDetailsType>('home', {
    mapFn: mapDtoToDashboardHomeDetailsType,
    lazy: true,
  });
  const { data: history, refetch: refetchHistory } = useApi<JobOrdersHistoryType>(
    'job-orders-history',
    {
      mapFn: mapDtoToJobOrdersHistoryType,
      lazy: true,
    },
  );

  useEffect(() => {
    refetchHome();
    if (isHistory) refetchHistory();
  }, [pathname, isHistory, refetchHome, refetchHistory]);

  const value = useMemo(() => ({ home, history }), [home, history]);
  return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>;
};

export const useDashboardData = () => useContext(DashboardDataContext);
